import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {BusinessPoolAccount} from '@/types';
import * as formatDate from '@/utils/date';

import PoolAccountSummary from './PoolAccountSummary';
type ExtraRowArg = BusinessPoolAccount;

const BusinessPoolAccountsTable = ({
  items,
}: {
  items: BusinessPoolAccount[];
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
}) => {
  return (
    <Table
      containerDataCy="merchant-pool-accounts-table"
      className="overflow-hidden"
    >
      <Thead>
        <Tr>
          <Th className="w-20">&nbsp;</Th>
          <Th>Account Name</Th>
          <Th>Account Number</Th>
          <Th>Country</Th>
          <Th>Status</Th>
          <Th>Date</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <BusinessPoolAccountsRow key={index} item={item} index={index} />
        ))}
      </TBody>
    </Table>
  );
};

const BusinessPoolAccountsRow = ({
  item,
  index,
}: {
  item: BusinessPoolAccount;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  return (
    <>
      <Tr className={zebraRow} data-cy="merchant-pool-accounts-row">
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            dataCy="merchant-pool-accounts-row-expand"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td data-cy="merchant-pool-accounts-row-account-name">
          {item?.accountName}
        </Td>
        <Td data-cy="merchant-pool-accounts-row-account-number">
          {item?.accountNumber}
        </Td>
        <Td data-cy="merchant-pool-accounts-row-country">
          {item?.countryCode}
        </Td>
        <Td data-cy="merchant-pool-accounts-row-status">
          {!item?.meta?.flags?.isDeactivated ? 'ACTIVE' : 'INACTIVE'}
        </Td>
        <Td>{formatDate.DDMonYYYY(String(item.updatedAt))}</Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <PoolAccountSummary item={item} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default BusinessPoolAccountsTable;
